import React, { useEffect, useState } from "react";
import { X, Send, Loader2, Mail, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/jobCalc";

/**
 * "Monthly statement run" — one-click bulk send of statement PDFs.
 *
 * Loads the preview target list first (which customers would receive one,
 * with balance + email status), then lets the admin fire the batch. Handles
 * three per-customer outcomes: sent / skipped (no email) / failed.
 */
export const StatementBatchRunModal = ({ onClose }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState(today);
  const [onlyWithBalance, setOnlyWithBalance] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busyPreview, setBusyPreview] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState("");

  const loadPreview = async () => {
    setBusyPreview(true);
    try {
      const data = await adminApi.statementBatchPreview({
        date_from: dateFrom || null,
        date_to: dateTo || null,
        only_with_balance: onlyWithBalance,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusyPreview(false);
    }
  };

  useEffect(() => {
    loadPreview();
  }, [dateFrom, dateTo, onlyWithBalance]);

  const sendable = (preview?.targets || []).filter((t) => t.has_email);

  const runBatch = async () => {
    if (sendable.length === 0) {
      toast.error("No customers with a valid email — nothing to send.");
      return;
    }
    // Confirm the destructive-ish action once
    if (!window.confirm(
      `Send ${sendable.length} statement email${sendable.length === 1 ? "" : "s"} ` +
      `totalling ${money(sendable.reduce((s, t) => s + t.balance, 0))}?`,
    )) return;
    setRunning(true);
    setResult(null);
    try {
      // Pin the exact customer list the admin previewed — protects against
      // data drift between preview and send.
      const res = await adminApi.statementBatchRun({
        date_from: dateFrom || null,
        date_to: dateTo || null,
        only_with_balance: onlyWithBalance,
        customer_ids: sendable.map((t) => t.customer_id),
        message: message.trim() || null,
      });
      setResult(res);
      const { sent, skipped, failed } = res.totals;
      if (failed > 0) {
        toast.error(`${sent} sent · ${skipped} skipped · ${failed} failed — see reasons below.`);
      } else {
        toast.success(`${sent} sent · ${skipped} skipped · ${failed} failed`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch run failed");
    } finally {
      setRunning(false);
    }
  };

  const targets = preview?.targets || [];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="statement-batch-modal"
    >
      <div className="bg-white rounded-md shadow-xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-[#cfd7db] flex items-start justify-between shrink-0">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">
              Monthly statement run
            </div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">
              Send every open account a statement
            </h2>
            <p className="text-xs text-[#3f4b52] mt-1">
              Attaches each customer&apos;s up-to-date statement PDF and emails it via Resend.
            </p>
          </div>
          <button onClick={onClose} className="text-[#3f4b52]" aria-label="Close" data-testid="stmt-batch-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
                data-testid="stmt-batch-from"
              />
            </div>
            <div>
              <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
                data-testid="stmt-batch-to"
              />
            </div>
            <label className="text-xs flex items-center gap-2 h-10 mt-5">
              <input
                type="checkbox"
                checked={onlyWithBalance}
                onChange={(e) => setOnlyWithBalance(e.target.checked)}
                data-testid="stmt-batch-only-balance"
              />
              Only customers with outstanding balance
            </label>
            <div className="flex justify-end mt-5">
              <button
                onClick={loadPreview}
                disabled={busyPreview}
                data-testid="stmt-batch-refresh"
                className="text-xs uppercase font-display tracking-widest px-3 h-10 border border-[#cfd7db] rounded-md hover:bg-[#f5f6f6] inline-flex items-center gap-1"
              >
                {busyPreview ? <Loader2 size={12} className="animate-spin" /> : null}
                Refresh preview
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-5 grid grid-cols-3 gap-3">
            <SummaryStat label="Customers" value={preview?.customer_count ?? "—"} />
            <SummaryStat label="Total outstanding" value={preview ? money(preview.total_balance) : "—"} accent="text-[#dc2626]" />
            <SummaryStat label="Sendable" value={preview ? `${preview.customers_with_email}` : "—"} accent="text-[#10b981]" />
          </div>

          {preview && preview.customers_without_email > 0 && (
            <div className="mt-3 flex items-start gap-2 text-xs bg-[#fff7ed] border border-[#fde3ba] p-3 rounded-md text-[#78350f]">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                <b>{preview.customers_without_email}</b> customer{preview.customers_without_email === 1 ? "" : "s"}
                {preview.customers_without_email === 1 ? " has " : " have "}
                no email address on file and will be skipped. Add an email under Customers → Edit to include them next run.
              </span>
            </div>
          )}

          {/* Optional intro */}
          <div className="mt-5">
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              Extra message (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              placeholder="e.g. Please pay before month-end to avoid interest."
              className="w-full mt-1 px-3 py-2 border border-[#cfd7db] rounded-md text-sm"
              data-testid="stmt-batch-message"
            />
          </div>

          {/* Preview list */}
          <div className="mt-5">
            <div className="text-xs uppercase font-display tracking-widest text-[#3f4b52] mb-2">
              Preview · {targets.length} customer{targets.length === 1 ? "" : "s"}
            </div>
            <div className="border border-[#cfd7db] rounded-md overflow-hidden">
              {targets.length === 0 && !busyPreview && (
                <div className="p-6 text-center text-sm text-[#3f4b52]">
                  Nothing outstanding — no statements would be sent.
                </div>
              )}
              {targets.map((t, i) => {
                const outcome = result ? getOutcome(result, t.customer_id) : null;
                const reason = result ? getReason(result, t.customer_id) : null;
                return (
                  <div
                    key={t.customer_id}
                    data-testid={`stmt-batch-row-${t.code || t.customer_id}`}
                    className={`px-4 py-2.5 flex items-center gap-3 text-sm ${
                      i > 0 ? "border-t border-[#eef2f4]" : ""
                    } ${outcome === "sent" ? "bg-[#f0fdf4]" : outcome === "failed" ? "bg-[#fef2f2]" : outcome === "skipped" ? "bg-[#f5f6f6]" : ""}`}
                  >
                    {t.code && (
                      <span className="font-mono text-xs font-bold text-[#f28a16] w-14 shrink-0">
                        {t.code}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-[#111619] truncate">{t.name}</div>
                      {reason && (outcome === "failed" || outcome === "skipped") && (
                        <div
                          className={`text-[11px] mt-0.5 truncate ${outcome === "failed" ? "text-[#dc2626]" : "text-[#3f4b52]"}`}
                          data-testid={`stmt-batch-reason-${t.code || t.customer_id}`}
                          title={reason}
                        >
                          {outcome === "failed" ? "Failed: " : "Skipped: "}{reason}
                        </div>
                      )}
                    </div>
                    {t.email ? (
                      <span className="text-xs text-[#3f4b52] hidden sm:inline">
                        <Mail size={10} className="inline mr-1" />
                        {t.email}
                      </span>
                    ) : (
                      <span className="text-xs text-[#dc2626] uppercase tracking-widest hidden sm:inline">
                        no email
                      </span>
                    )}
                    <span className="text-xs text-[#3f4b52]">
                      {t.invoice_count} inv
                    </span>
                    <span className="font-display font-black text-[#dc2626] w-24 text-right">
                      {money(t.balance)}
                    </span>
                    <span className="w-6 flex justify-center">
                      {outcome === "sent" && <CheckCircle2 size={16} className="text-[#10b981]" />}
                      {outcome === "failed" && <XCircle size={16} className="text-[#dc2626]" />}
                      {outcome === "skipped" && <span className="text-xs text-[#3f4b52]">—</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Result summary */}
          {result && (
            <>
              <div className="mt-5 grid grid-cols-3 gap-3">
                <ResultStat label="Sent" value={result.totals.sent} color="text-[#10b981]" />
                <ResultStat label="Skipped" value={result.totals.skipped} color="text-[#3f4b52]" />
                <ResultStat label="Failed" value={result.totals.failed} color="text-[#dc2626]" />
              </div>
              {result.failed.length > 0 && (
                <div className="mt-4 border border-[#fecaca] rounded-md p-3 bg-[#fef2f2]" data-testid="stmt-batch-failures">
                  <div className="text-xs uppercase font-display tracking-widest text-[#dc2626] flex items-center gap-2 mb-2">
                    <XCircle size={14} /> Failures — {result.failed.length}
                  </div>
                  <ul className="space-y-1 text-xs">
                    {result.failed.map((f) => (
                      <li key={f.customer_id} className="flex items-start gap-2">
                        <span className="font-mono font-bold text-[#f28a16] shrink-0 w-14">{f.code || "—"}</span>
                        <span className="font-display font-bold text-[#111619] shrink-0">{f.name}</span>
                        <span className="text-[#dc2626]">— {f.reason || "unknown error"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-[#cfd7db] flex items-center justify-between shrink-0 bg-[#f5f6f6]">
          <div className="text-xs text-[#3f4b52]">
            {sendable.length > 0 ? (
              <>
                Will email <b className="text-[#111619]">{sendable.length}</b> customer{sendable.length === 1 ? "" : "s"} · total{" "}
                <b className="text-[#dc2626]">{money(sendable.reduce((s, t) => s + t.balance, 0))}</b>
              </>
            ) : (
              <>Nothing sendable in the current filter.</>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={running}
              className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-white"
              data-testid="stmt-batch-cancel"
            >
              Close
            </button>
            <button
              onClick={runBatch}
              disabled={running || sendable.length === 0}
              data-testid="stmt-batch-send"
              className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {running ? "Sending…" : `Send ${sendable.length || ""} statement${sendable.length === 1 ? "" : "s"}`.trim()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, accent }) => (
  <div className="bg-[#f5f6f6] border border-[#cfd7db] rounded-md px-4 py-3">
    <div className="text-[10px] font-display uppercase tracking-widest text-[#3f4b52]">{label}</div>
    <div className={`text-2xl font-display font-black mt-1 ${accent || "text-[#111619]"}`}>{value}</div>
  </div>
);

const ResultStat = ({ label, value, color }) => (
  <div className="border border-[#cfd7db] rounded-md px-4 py-3 text-center">
    <div className="text-[10px] font-display uppercase tracking-widest text-[#3f4b52]">{label}</div>
    <div className={`text-3xl font-display font-black mt-1 ${color}`}>{value}</div>
  </div>
);

const getOutcome = (result, customer_id) => {
  if (result.sent.some((x) => x.customer_id === customer_id)) return "sent";
  if (result.failed.some((x) => x.customer_id === customer_id)) return "failed";
  if (result.skipped.some((x) => x.customer_id === customer_id)) return "skipped";
  return null;
};

const getReason = (result, customer_id) => {
  const f = result.failed.find((x) => x.customer_id === customer_id);
  if (f?.reason) return f.reason;
  const s = result.skipped.find((x) => x.customer_id === customer_id);
  return s?.reason || null;
};

export default StatementBatchRunModal;
